import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { userHasRole } from '../utils/roleHelpers';
import { Plus, Trash2, Search, MoreHorizontal, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileSpreadsheet, Clock, Filter, X, Lock } from 'lucide-react';
import { leadsAPI, notificationsAPI } from '../services/api';
import api from '../services/api';
import LeadTable from '../components/LeadTable';
import LeadModal from '../components/LeadModal';
import LeadImportExportModal from '../components/LeadImportExportModal';
import AdvancedFilterPanel from '../components/AdvancedFilterPanel';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyFiltersFromURL, hasURLFilters } from '../utils/filterUtils';

const STORAGE_KEY = 'leads_advanced_filters';
const SEARCH_STORAGE_KEY = 'leads_search_term';
const PAGE_STORAGE_KEY = 'leads_current_page';
const FILTER_OPEN_KEY = 'leads_filter_open';

const loadStoredFilters = () => {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
};
const saveStoredFilters = (filters) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
};

const Leads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = userHasRole(user, 'admin');
  const isExecutive = (user?.role || '').toLowerCase() === 'executive';
  const canBulkDelete = isAdmin || isExecutive;
  // Import/Export is restricted to Admin & Executive only.
  const canImportExport = isAdmin || isExecutive;
  // Phase 12 — qc and cx_leader have strict view-only access to Accounts.
  // iter251 — migrated to permission-based gating so any role the admin
  // grants `leads.edit` / `leads.create` behaves as non-view-only without
  // a code change. `isViewOnlyAccounts` derives from the effective
  // `leads.edit` permission; `canAddAccount` derives from `leads.create`.
  const { canEdit, canCreate } = usePermissions();
  const isViewOnlyAccounts = !(isAdmin || canEdit('leads'));
  const canAddAccount = isAdmin || canCreate('leads');

  const [allLeads, setAllLeads] = useState([]);
  const [loadError, setLoadError] = useState(null); // iter291 — surfaced instead of stale data
  const [skippedCount, setSkippedCount] = useState(0); // iter291 — malformed rows the API had to skip
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem(SEARCH_STORAGE_KEY) || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(() => {
    const stored = localStorage.getItem(PAGE_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);
  // Draft chooser state — gates the "Add Account" flow when a server-side
  // draft exists for the logged-in user. We deliberately keep this state
  // local to the page (not the modal) because the prompt must appear
  // BEFORE the modal opens, per the Accounts spec.
  const [isDraftChooserOpen, setIsDraftChooserOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null); // server draft payload
  const [draftInitial, setDraftInitial] = useState(null); // what we pass into LeadModal

  // Restore filters from URL params first, then fall back to localStorage
  const [isFilterOpen, setIsFilterOpen] = useState(() => localStorage.getItem(FILTER_OPEN_KEY) === 'true');
  const [activeFilters, setActiveFilters] = useState(() => {
    if (hasURLFilters(searchParams)) {
      return applyFiltersFromURL(searchParams);
    }
    return loadStoredFilters();
  });
  const [urlFiltersApplied, setUrlFiltersApplied] = useState(() => hasURLFilters(searchParams));

  const [overdueLeads, setOverdueLeads] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [lockFilter, setLockFilter] = useState('all'); // all | locked | unlocked
  // iter275 — Account "Category" preset. Values:
  //   'all'         → no filter
  //   'meta_leads'  → filter leads by source=Meta
  //   'closed_won'  → filter leads by stage=Closed Won
  //   any other id  → treat as a lead_stage from Settings → Lead Stages
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [customStages, setCustomStages] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/settings/lead-stages');
        setCustomStages(Array.isArray(r.data) ? r.data : []);
      } catch { /* stages settings optional */ }
    })();
  }, []);
  const [approvedCount, setApprovedCount] = useState(0);
  const [duplicateMap, setDuplicateMap] = useState({});
  const filterBtnRef = useRef(null);

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const activeFilterCount = Object.keys(activeFilters).length;

  // Persist filter open state
  useEffect(() => { localStorage.setItem(FILTER_OPEN_KEY, isFilterOpen ? 'true' : 'false'); }, [isFilterOpen]);

  // Auto-open filter panel when URL filters are applied
  useEffect(() => {
    if (urlFiltersApplied && Object.keys(activeFilters).length > 0) {
      setIsFilterOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist search term
  useEffect(() => { localStorage.setItem(SEARCH_STORAGE_KEY, searchTerm); }, [searchTerm]);

  // Debounced search — one backend request per pause in typing instead of
  // one per keystroke. 300ms matches the global search behavior.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Persist current page
  useEffect(() => { localStorage.setItem(PAGE_STORAGE_KEY, String(currentPage)); }, [currentPage]);

  const fetchOverdueLeads = useCallback(async () => {
    try { const res = await notificationsAPI.getOverdueLeads(); setOverdueLeads(res.data || []); } catch { /* silent */ }
  }, []);

  useEffect(() => { refreshLeads(); }, [currentPage, itemsPerPage, debouncedSearchTerm, activeFilters, lockFilter, categoryFilter]);

  const fetchApprovedCount = useCallback(async () => {
    try { const res = await leadsAPI.approvedCount(); setApprovedCount(res.data?.count || 0); } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchApprovedCount(); }, [fetchApprovedCount, allLeads]);

  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await leadsAPI.duplicates();
      const idMap = res.data?.id_map || {};
      const approved = new Set(res.data?.approved_ids || []);
      const map = {};
      Object.entries(idMap).forEach(([id, info]) => {
        map[id] = { color: info.color, match_type: info.match_type, group: info.group, approved: approved.has(id) };
      });
      setDuplicateMap(map);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates, allLeads]);

  const handleApproveDuplicate = async (lead) => {
    try {
      const grp = duplicateMap[lead.id]?.group;
      const memberIds = grp
        ? Object.entries(duplicateMap).filter(([, v]) => v.group === grp).map(([id]) => id)
        : [lead.id];
      await Promise.all(memberIds.map(id => leadsAPI.approveDuplicate(id)));
      toast.success('Duplicate group approved as separate accounts');
      fetchDuplicates();
    } catch { toast.error('Failed to approve'); }
  };
  const handleDeleteDuplicate = async (lead) => {
    await handleDelete(lead.id);
    fetchDuplicates();
  };

  // Cluster duplicate-group members adjacent to each other in the table.
  const displayLeads = useMemo(() => {
    if (!Object.keys(duplicateMap).length) return allLeads;
    return [...allLeads].sort((a, b) => {
      const ga = duplicateMap[a.id]?.group || '';
      const gb = duplicateMap[b.id]?.group || '';
      if (ga && gb) return ga < gb ? -1 : ga > gb ? 1 : 0;
      if (ga) return -1;
      if (gb) return 1;
      return 0;
    });
  }, [allLeads, duplicateMap]);

  useEffect(() => {
    fetchOverdueLeads();
    const interval = setInterval(fetchOverdueLeads, 30000);
    return () => clearInterval(interval);
  }, [fetchOverdueLeads]);

  // Fetch labels master list for display in table and filters
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/labels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAllLabels(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const overdueMap = useMemo(() => {
    const map = {};
    overdueLeads.forEach(ol => { map[ol.lead_id] = ol; });
    return map;
  }, [overdueLeads]);

  // Phase 10 — clock icon is shown ONLY to gtm_sales / gtm_sales_leader,
  // and ONLY when at least one of the 5 mandatory documents is not yet
  // 'Complete'. Optional docs (NOC / Lease / Electricity / GST) are
  // explicitly ignored.
  const userRole = (user?.role || '').toLowerCase();
  const isGTM = userRole === 'gtm_sales' || userRole === 'gtm_sales_leader';
  const mandatoryPendingMap = useMemo(() => {
    if (!isGTM) return {};
    const MANDATORY = ['master_form', 'pan_card', 'aadhar_card', 'cancelled_check_and_bank_details', 'property_photos'];
    const isComplete = (v) => typeof v === 'string' && v.trim().toLowerCase() === 'complete';
    const map = {};
    allLeads.forEach((ld) => {
      const checklist = ld.property_checklist_info || {};
      if (MANDATORY.some((k) => !isComplete(checklist[k]))) {
        map[ld.id] = true;
      }
    });
    return map;
  }, [allLeads, isGTM]);

  const refreshRequestIdRef = useRef(0);
  const refreshLeads = async () => {
    const reqId = ++refreshRequestIdRef.current; // stale-response guard
    try {
      setLoading(true);
      setLoadError(null);
      const limit = itemsPerPage === 'all' ? 10000 : itemsPerPage;
      const page = itemsPerPage === 'all' ? 1 : currentPage;

      const lockParam = lockFilter !== 'all' ? lockFilter : undefined;
      // iter275 — translate the Category preset into the correct query
      // param. 'meta_leads' → source=Meta; anything else non-'all' →
      // lead_stage filter (works for the two seed presets Closed Won +
      // Meta Leads AND for every custom stage admin adds later).
      let sourceParam, stageParam;
      if (categoryFilter === 'meta_leads') { sourceParam = 'Meta'; }
      else if (categoryFilter === 'closed_won') { stageParam = 'Closed Won'; }
      else if (categoryFilter && categoryFilter !== 'all') { stageParam = categoryFilter; }

      let responseData;
      if (hasActiveFilters) {
        const response = await leadsAPI.filterAdvanced({ page, limit, search: debouncedSearchTerm, filters: activeFilters, lock_status: lockParam, source: sourceParam, stage: stageParam });
        if (reqId !== refreshRequestIdRef.current) return; // newer request in flight
        responseData = response.data;
      } else {
        const response = await leadsAPI.getAll({ page, limit, search: debouncedSearchTerm, lock_status: lockParam, source: sourceParam, stage: stageParam });
        if (reqId !== refreshRequestIdRef.current) return; // newer request in flight
        responseData = response.data;
      }
      setAllLeads(responseData.data || []);
      setTotalItems(responseData.total || 0);
      // iter291 — surface skipped-record count only when the API reports one.
      const skipped = Number(responseData.skipped || 0);
      setSkippedCount(skipped);
      if (skipped > 0) {
        toast.warning(`Loaded ${responseData.total} accounts (${skipped} could not be displayed due to malformed data). Contact an admin.`);
      }
      setSelectedIds([]);
    } catch (error) {
      if (reqId !== refreshRequestIdRef.current) return; // ignore stale
      // iter291 — surface the failure instead of silently keeping stale data.
      console.error('Error fetching leads:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      const msg = `Failed to load accounts: ${detail}`;
      setLoadError(msg);
      setAllLeads([]);   // clear so the UI doesn't lie
      setTotalItems(0);
      toast.error(msg);
    } finally {
      if (reqId === refreshRequestIdRef.current) setLoading(false);
    }
  };

  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalItems / itemsPerPage);

  const handleSearch = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const handleDelete = async (id) => {
    if (!canBulkDelete) return;
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await leadsAPI.delete(id);
        toast.success('Account deleted');
        refreshLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
        toast.error('Failed to delete account');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete) return;
    if (selectedIds.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} selected account(s)? This cannot be undone.`)) {
      try {
        await leadsAPI.bulkDelete(selectedIds);
        toast.success(`${selectedIds.length} account(s) deleted`);
        setSelectedIds([]);
        refreshLeads();
      } catch (error) {
        console.error('Error bulk deleting leads:', error);
        toast.error('Failed to delete accounts');
      }
    }
  };

  const handleEdit = (lead) => { setCurrentLead(lead); setDraftInitial(null); setIsModalOpen(true); };

  // Add Account → ask the user whether to continue a previously saved
  // draft or start blank. We hit /api/account-drafts/me; on 204 (no draft)
  // we skip the prompt and open a blank modal directly.
  const handleAdd = async () => {
    setCurrentLead(null);
    setDraftInitial(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account-drafts/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 200) {
        const body = await res.json().catch(() => null);
        if (body && body.data) {
          setPendingDraft(body);
          setIsDraftChooserOpen(true);
          return;
        }
      }
    } catch {
      /* network errors fall through to "open blank modal" */
    }
    setIsModalOpen(true);
  };

  // User chose "Continue Draft" — hydrate the modal from the saved draft.
  const handleContinueDraft = () => {
    setDraftInitial(pendingDraft?.data || null);
    setIsDraftChooserOpen(false);
    setIsModalOpen(true);
  };

  // User chose "Start New" — discard the server draft, then open blank modal.
  const handleStartNew = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account-drafts/me`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {
      /* best-effort delete; backend is idempotent */
    }
    setPendingDraft(null);
    setDraftInitial(null);
    setIsDraftChooserOpen(false);
    setIsModalOpen(true);
  };

  const handleSave = async (leadData) => {
    try {
      // Phase 18a.1 — extract the (optional) staged PEF file before sending
      // the JSON payload to /api/leads. The upload endpoint requires the
      // lead_id which we only have after the create call returns.
      const pendingPefFile = leadData._pendingPefFile || null;
      const cleanLeadData = { ...leadData };
      delete cleanLeadData._pendingPefFile;

      let savedLeadId = null;

      if (currentLead) {
        const wasClosedWon = (currentLead.lead_stage || '').toUpperCase() === 'CLOSED WON';
        await leadsAPI.update(currentLead.id, cleanLeadData);
        savedLeadId = currentLead.id;
        if (!wasClosedWon && (cleanLeadData.lead_stage || '').toUpperCase() === 'CLOSED WON') {
          toast.success('Lead updated to Closed Won — Lifecycle auto-created', { description: `${cleanLeadData.hotel_name || currentLead.hotel_name} added to Hotel Lifecycle tab` });
        } else {
          toast.success('Account updated', { description: `${cleanLeadData.hotel_name || currentLead.hotel_name} saved successfully` });
        }
      } else {
        const createRes = await leadsAPI.create(cleanLeadData);
        savedLeadId = createRes?.data?.id || null;
        const isClosedWon = (cleanLeadData.lead_stage || '').toUpperCase() === 'CLOSED WON';
        const role = (user?.role || '').toLowerCase();
        const willAutoCreateLifecycle = isClosedWon || role === 'gtm_sales' || role === 'gtm_sales_leader';
        if (isClosedWon) {
          toast.success('Closed Won lead created — Lifecycle auto-created', { description: `${cleanLeadData.hotel_name} added to Hotel Lifecycle tab` });
        } else if (willAutoCreateLifecycle) {
          toast.success('Account created — added to Hotel Lifecycle', { description: `${cleanLeadData.hotel_name} is now visible in the Hotel Lifecycle tab` });
        } else {
          toast.success('Account created', { description: `${cleanLeadData.hotel_name} saved successfully` });
        }
      }

      // Upload any PEF file the user staged in the modal. Best-effort: a
      // failure here does NOT roll back the account that was just saved.
      if (pendingPefFile && savedLeadId) {
        try {
          const fd = new FormData();
          fd.append('file', pendingPefFile);
          const token = localStorage.getItem('token') || localStorage.getItem('access_token');
          const resp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/leads/${savedLeadId}/upload-pef-pdf`,
            {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: fd,
            },
          );
          if (!resp.ok) {
            const errText = await resp.text();
            let errMsg = 'PEF upload failed';
            try { errMsg = JSON.parse(errText)?.detail || errMsg; } catch { errMsg = errText || errMsg; }
            toast.error('PEF document upload failed', { description: errMsg });
          } else {
            toast.success('PEF document uploaded');
          }
        } catch (pefErr) {
          toast.error('PEF document upload failed', { description: pefErr.message });
        }
      }

      setIsModalOpen(false);
      refreshLeads();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Failed to save lead', { description: error.response?.data?.detail?.message || error.message });
    }
  };

  const handleSelectOne = (id, selected) => {
    setSelectedIds(prev => selected ? [...prev, id] : prev.filter(i => i !== id));
  };
  const handleSelectAll = (selected) => {
    setSelectedIds(selected ? allLeads.map(l => l.id) : []);
  };

  // Inline property status change — update backend + local state immediately
  const handleInlineStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { property_status: newStatus });
      setAllLeads(prev => prev.map(l => l.id === leadId ? { ...l, property_status: newStatus } : l));
    } catch (err) {
      console.error('Failed to update property status:', err);
      toast.error('Failed to update property status');
    }
  };

  // Inline labels change — update backend + local state immediately
  const handleInlineLabelsChange = async (leadId, newLabels) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/labels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ labels: newLabels }),
      });
      setAllLeads(prev => prev.map(l => l.id === leadId ? { ...l, labels: newLabels } : l));
    } catch (err) {
      console.error('Failed to update labels:', err);
      toast.error('Failed to update labels');
    }
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    saveStoredFilters(filters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    saveStoredFilters({});
    setCurrentPage(1);
    // Clear URL filter params if present
    if (urlFiltersApplied) {
      setSearchParams({}, { replace: true });
      setUrlFiltersApplied(false);
    }
  };

  // Toggle only from the dedicated filter button — never from child clicks
  const toggleFilterPanel = (e) => {
    e.stopPropagation();
    setIsFilterOpen(prev => !prev);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] space-y-3 overflow-hidden max-w-full" data-testid="leads-page">
      {/* iter291 — Load-error banner. Rendered above the header so it's the
          first thing a user sees when the accounts list fails to hydrate. */}
      {loadError && (
        <div
          role="alert"
          data-testid="leads-load-error"
          className="flex items-start justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
        >
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Couldn't load accounts</div>
            <div className="text-red-600 truncate">{loadError}</div>
          </div>
          <button
            type="button"
            onClick={refreshLeads}
            className="px-3 py-1.5 text-xs font-semibold bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50 shrink-0"
            data-testid="leads-load-retry"
          >
            Retry
          </button>
        </div>
      )}
      {!loadError && skippedCount > 0 && (
        <div
          role="status"
          data-testid="leads-skipped-warning"
          className="px-4 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm"
        >
          <span className="font-semibold">{skippedCount}</span> account{skippedCount === 1 ? '' : 's'} could not be shown because of malformed data — please contact an admin.
        </div>
      )}
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Accounts</h1>
          {isViewOnlyAccounts && (
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="accounts-readonly-badge">
              View-only access
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          {canBulkDelete && selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} className="flex items-center px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm" data-testid="bulk-delete-btn">
              <Trash2 size={16} className="mr-1.5" /> Delete Account ({selectedIds.length})
            </button>
          )}
          <div className="relative flex-1 sm:w-56 min-w-[180px]">
            <input type="text" placeholder="Search by Hotel Name or GRB ID..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
              value={searchTerm} onChange={handleSearch} data-testid="search-input" />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          </div>

          {/* iter275 — Account Category filter. Works together with search + advanced filters + lock filter + pagination. */}
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
            data-testid="accounts-category-filter"
            title="Filter accounts by category (Meta source or Lead Stage)"
          >
            <option value="all">All</option>
            <option value="meta_leads">Meta Leads</option>
            <option value="closed_won">Closed Won</option>
            {customStages
              .filter((s) => (s.name || '').toLowerCase() !== 'closed won')
              .map((s) => (
                <option key={s.id || s.name} value={s.name}>{s.name}</option>
              ))}
          </select>

          <button ref={filterBtnRef} onClick={toggleFilterPanel}
            className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg transition-colors whitespace-nowrap text-sm ${
              hasActiveFilters ? 'bg-teal-50 border-teal-300 text-teal-700 hover:bg-teal-100' : isFilterOpen ? 'bg-gray-100 border-gray-400 text-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="toggle-filter-btn">
            <Filter size={15} />
            <span className="font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-teal-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">{activeFilterCount}</span>
            )}
          </button>

          <button onClick={handleAdd} disabled={!canAddAccount} className={`flex items-center px-3 py-2 rounded-lg transition-colors shadow-sm whitespace-nowrap text-sm ${canAddAccount ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'}`} data-testid="add-lead-btn">
            <Plus size={18} className="mr-1.5" /> Add Account
          </button>
          {canImportExport && (
            <button onClick={() => setIsImportExportOpen(true)} className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap text-sm" data-testid="import-export-btn">
              <FileSpreadsheet size={16} className="mr-1.5" /> Import / Export
            </button>
          )}
          <button className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"><MoreHorizontal size={18} /></button>
        </div>
      </div>

      {/* Advanced Filter Panel — sticky with max-height */}
      <div className="flex-shrink-0">
        <AdvancedFilterPanel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} onApply={handleApplyFilters} activeFilters={activeFilters} />
      </div>

      {/* Active filter summary bar (when panel is closed) */}
      {hasActiveFilters && !isFilterOpen && (
        <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0" data-testid="filter-summary-bar">
          <Filter size={14} className="text-teal-600 flex-shrink-0" />
          <span className="text-xs text-teal-700 font-medium">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
          {urlFiltersApplied && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-200 text-teal-800 font-semibold" data-testid="url-filter-badge">via Insights</span>}
          <span className="text-xs text-teal-500">({totalItems} results)</span>
          <div className="flex-1" />
          <button onClick={() => setIsFilterOpen(true)} className="text-xs text-teal-600 hover:text-teal-800 font-medium">Edit</button>
          <button onClick={handleClearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5" data-testid="clear-filters-summary">
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Controls Row */}
      <div className="flex justify-between items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-600 pl-2">Show</span>
          <select className="border border-gray-300 rounded-md text-sm p-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
            value={itemsPerPage} onChange={(e) => { setItemsPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }} data-testid="items-per-page">
            <option value={25}>25</option><option value={50}>50</option><option value={75}>75</option><option value={100}>100</option><option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
          <span className="text-xs text-gray-400 ml-2">({totalItems} total{hasActiveFilters ? ' filtered' : ''})</span>

          {/* Approved Accounts card */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 ml-1" data-testid="approved-accounts-card">
            <Lock size={14} className="text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">Approved Accounts</span>
            <span className="text-sm font-bold text-emerald-800" data-testid="approved-accounts-count">{approvedCount}</span>
          </div>

          {/* Lock / Unlock quick filter */}
          <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden" data-testid="lock-filter">
            {[
              { id: 'all', label: 'All' },
              { id: 'locked', label: 'Locked' },
              { id: 'unlocked', label: 'Unlocked' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => { setLockFilter(opt.id); setCurrentPage(1); }}
                data-testid={`lock-filter-${opt.id}`}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  lockFilter === opt.id ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={refreshLeads} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-full transition-colors" title="Refresh" data-testid="refresh-btn">
            <RefreshCw size={18} />
          </button>
          {itemsPerPage !== 'all' && totalPages > 1 && (
            <div className="flex items-center border rounded-md overflow-hidden">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
              <span className="px-4 py-1 text-sm font-medium text-gray-700 bg-gray-50">Page {currentPage} of {totalPages || 1}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Lead Table — fills remaining space with internal scroll */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-0">
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>
        ) : allLeads.length > 0 ? (
          <div className="flex-1 overflow-auto min-h-0">
            <LeadTable leads={displayLeads} selectedIds={selectedIds} onSelectOne={handleSelectOne} onSelectAll={handleSelectAll} onEdit={handleEdit} onDelete={handleDelete} overdueMap={overdueMap} mandatoryPendingMap={mandatoryPendingMap} showClockColumn={isGTM} allLabels={allLabels} onStatusChange={handleInlineStatusChange} onLabelsChange={handleInlineLabelsChange} duplicateMap={duplicateMap} onApproveDuplicate={handleApproveDuplicate} onDeleteDuplicate={handleDeleteDuplicate} />
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <Search size={48} className="text-gray-300 mb-4" />
            <p className="text-lg font-medium">No leads found</p>
            <p className="text-sm">{hasActiveFilters ? 'No leads match your filter criteria. Try adjusting your filters.' : 'Try adjusting your search or add a new lead.'}</p>
            {hasActiveFilters && (
              <button onClick={handleClearFilters} className="mt-3 text-sm text-teal-600 hover:text-teal-800 font-medium">Clear all filters</button>
            )}
          </div>
        )}
        <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between flex-shrink-0">
          <span>Showing {allLeads.length > 0 ? (itemsPerPage === 'all' ? 1 : (currentPage - 1) * itemsPerPage + 1) : 0} to {itemsPerPage === 'all' ? totalItems : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries{hasActiveFilters ? ' (filtered)' : ''}</span>
          <span>Total Leads: {totalItems}</span>
        </div>
      </div>

      <LeadModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setDraftInitial(null); }} onSave={handleSave} lead={currentLead} initialDraft={draftInitial} />

      {/* Draft chooser — only renders when a server-side draft exists for
          the current user and they clicked "Add Account". Keeps draft
          loading explicit; no auto-restore. */}
      {isDraftChooserOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="draft-chooser-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="draft-chooser-modal">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Saved draft found</h3>
              <button
                onClick={() => setIsDraftChooserOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                data-testid="draft-chooser-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-2">
              <p className="text-sm text-gray-700">
                You have an unfinished Account draft saved on
                <span className="font-medium text-gray-900">
                  {' '}{pendingDraft?.updatedAt ? new Date(pendingDraft.updatedAt).toLocaleString() : 'your account'}
                </span>.
              </p>
              <p className="text-sm text-gray-500">Continue editing it, or discard it and start a new Account.</p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={handleStartNew}
                type="button"
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors font-medium"
                data-testid="draft-chooser-start-new"
              >
                Start New (Discard Draft)
              </button>
              <button
                onClick={handleContinueDraft}
                type="button"
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                data-testid="draft-chooser-continue"
              >
                Continue Draft
              </button>
            </div>
          </div>
        </div>
      )}
      <LeadImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        onImportComplete={refreshLeads}
        currentFilters={{ ...(activeFilters || {}), search: searchTerm || undefined }}
        selectedIds={selectedIds}
      />
    </div>
  );
};

export default Leads;
